<template>
  <v-dialog
    scrollable
    v-model="open"
    max-width="819"
    @click:outside="$emit('close')"
  >
    <v-card>
      <v-card-title class="px-6 pt-4">
        <span class="font-weight-bold h6">
          {{ $t('routines.qr.routine_qr_code') }}
        </span>
      </v-card-title>
      <v-card-text
        class="d-flex justify-space-evenly align-center px-9 pt-8 pb-11"
      >
        <v-col cols="6">
          <QRCode class="" :value="url" size="353" level="L" />
        </v-col>
        <v-col cols="6" class="h-full mt-3 pl-6">
          <div
            class="d-flex flex-column align-start mb-4"
            v-if="routineDefinition.name"
          >
            <FieldLabel
              class="font-bold font-size-16"
              :text="routineDefinition.name"
            />
            <span class="blue-grey--text text--darken-2">{{
              routineDefinition.description
            }}</span>
          </div>

          <div v-if="showTags" class="mb-4">
            <FieldLabel
              class="font-weight-400"
              :text="$t('common.attribute_other')"
            />
            <div>
              <v-chip
                v-for="tag in presetTags"
                class="me-2 mt-2"
                outlined
                small
                :key="tag.id"
              >
                <span class="blue-grey--text text--darken-2">{{
                  tag.name
                }}</span>
              </v-chip>
            </div>
          </div>
          <div
            class="d-flex flex-column align-start mb-4"
            v-if="routineDefinition.type"
          >
            <FieldLabel class="font-weight-400" :text="$t('common.type')" />
            <v-chip class="mt-2" outlined small>
              <span class="blue-grey--text text--darken-2">{{
                $t(`routines.type.${routineDefinition.type}`)
              }}</span>
            </v-chip>
          </div>
          <div
            class="d-flex flex-column align-start mb-4"
            v-if="routineDefinition.frequency"
          >
            <FieldLabel
              class="font-weight-400"
              :text="$t('routines.frequency_label')"
            />
            <v-chip class="mt-2" outlined small>
              <span class="blue-grey--text text--darken-2">{{
                $t(`routines.frequency.${routineDefinition.frequency}`)
              }}</span>
            </v-chip>
          </div>
        </v-col>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="d-flex flex-column flex-row align-end py-6 px-4">
        <template>
          <v-menu offset-y left px-0>
            <template v-slot:activator="{ on }">
              <Button
                data-testid="qrCodeModal-button-download"
                class="pl-4 pr-2 py-3"
                color="primary"
                v-on="on"
                iconPosition="right"
                :loading="isLoading"
                :icon="collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                :text="$t('common.button.download')"
              />
            </template>
            <v-list nav dense class="px-0">
              <v-list-item
                data-testid="qrCodeModal-button-qrCodeOnly"
                @click="handleQr(exportType.PORTRAIT)"
              >
                <v-list-item-content class="py-0">
                  <v-list-item-title
                    class="grey--text text--darken-4 font-weight-medium"
                    >{{ $t('routines.qr.qr_code_only') }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                data-testid="qrCodeModal-button-qrCodeDetails"
                @click="handleQr(exportType.LANDSCAPE)"
              >
                <v-list-item-content class="py-0">
                  <v-list-item-title
                    class="grey--text text--darken-4 font-weight-medium"
                    >{{
                      $t('routines.qr.qr_code_with_details')
                    }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-card-actions>
      <QRPortrait
        v-if="shouldMountQr"
        class="printable"
        ref="qrPortrait"
        :qrUrl="url"
        :definition="routineDefinition"
      />
      <QRLandscape
        v-if="shouldMountQr"
        class="printable"
        ref="qrLandscape"
        :qrUrl="url"
        :definition="routineDefinition"
      />
    </v-card>
  </v-dialog>
</template>
<script>
import QRCode from 'qrcode.vue'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import _ from 'lodash'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import config from '@/config'
import { createLetterPdf } from '@/lib/pdf'
import { mapGetters } from 'vuex'

import Button from '@/components/UI/Button'
import QRPortrait from './QRPortrait.vue'
import QRLandscape from './QRLandscape.vue'
import FieldLabel from '@/components/Core/FieldLabel.vue'

const exportType = {
  LANDSCAPE: 'landscape',
  PORTRAIT: 'portrait',
}

export default {
  name: 'QRCodeModal',

  mixins: [mixin, trackerMixin],

  components: {
    Button,
    QRCode,
    QRPortrait,
    QRLandscape,
    FieldLabel,
  },

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    routineDefinition: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      collapsed: true,
      shouldMountQr: false,
      isLoading: false,
      exportType,
    }
  },
  computed: {
    ...mapGetters(['account', 'organizationId', 'organizationTags']),

    url() {
      const environmentURL = config.webAppURL
      return `${environmentURL}/routine-definitions/${this.routineDefinition.id}/start`
    },

    showTags() {
      return !_.isEmpty(this.presetTags)
    },

    presetTags() {
      return _.filter(this.organizationTags, tag =>
        _.includes(this.routineDefinition.presetTags, tag.id)
      )
    },
  },

  methods: {
    async handleQr(type) {
      try {
        this.isLoading = true
        this.shouldMountQr = true
        await this.$nextTick()
        const printableElement =
          type === this.exportType.LANDSCAPE
            ? this.$refs.qrLandscape.$el
            : this.$refs.qrPortrait.$el
        type === this.exportType.LANDSCAPE
          ? this.trackDownloadQr({ type: 'qrCodeDetails' })
          : this.trackDownloadQr({ type: 'qrCodeOnly' })
        const canvas = await html2canvas(printableElement, {
          type: 'dataURL',
        })
        const doc = createLetterPdf(
          type === this.exportType.LANDSCAPE ? 'l' : 'p',
          jsPDF
        )
        const imgData = canvas.toDataURL('image/png')
        type === this.exportType.LANDSCAPE
          ? doc.addImage(canvas.toDataURL('image/png'), 'PNG', 30, 30, 240, 140)
          : doc.addImage(imgData, 'PNG', 60, 50, 100, 140)
        doc.save(`${this.routineDefinition.name}-QR.pdf`)
      } catch (err) {
        this.showError(err.message)
      } finally {
        this.shouldMountQr = false
        this.isLoading = false
      }
    },
  },
}
</script>
<style scoped lang="scss">
.printable {
  z-index: -1;
  position: absolute;
  top: 1000;
}
</style>
